/**
 * W-042 — Community browser. Stories: US-A07.
 *
 * Browsing all posts independently of reports, with hide and delete
 * available inline — the same `moderation-content-action` edge function the
 * report detail screen uses, just with no report to resolve alongside it.
 */
import { useState } from 'react'
import { List, useTable } from '@refinedev/antd'
import { CanAccess, useInvalidate } from '@refinedev/core'
import { Alert, Button, Space, Table, Tag, Typography } from 'antd'
import dayjs from 'dayjs'
import { ModerationActionDialog } from '@/components/dialogs'
import type { ModerationAction } from '@/components/dialogs/ModerationActionDialog'
import { invokeEdgeFunction } from '@/lib/supabase'
import { useAdminTranslate } from '@/providers/i18n-provider'
import type { Post } from '@/types'

interface PostRow extends Post {
  profiles: { full_name: string } | null
  subjects: { name_fr: string } | null
}

export const CommunityBrowserPage = () => {
  const translate = useAdminTranslate()
  const invalidate = useInvalidate()
  const [target, setTarget] = useState<PostRow | null>(null)
  const [action, setAction] = useState<ModerationAction | null>(null)
  const [error, setError] = useState<string | null>(null)

  const { tableProps } = useTable<PostRow>({
    resource: 'posts',
    filters: { permanent: [{ field: 'is_deleted', operator: 'eq', value: false }] },
    sorters: { initial: [{ field: 'created_at', order: 'desc' }] },
    pagination: { pageSize: 25 },
    meta: { select: '*, profiles(full_name), subjects(name_fr)' },
    syncWithLocation: true,
  })

  function openAction(row: PostRow, next: ModerationAction) {
    setTarget(row)
    setAction(next)
  }

  async function confirm(reason: string) {
    if (!target || !action) return
    try {
      await invokeEdgeFunction('moderation-content-action', {
        report_ids: [],
        target_type: 'post',
        target_id: target.id,
        action,
        reason,
      })
      void invalidate({ resource: 'posts', invalidates: ['list'] })
    } catch {
      setError(translate('admin.screens.moderation.actionFailed'))
    } finally {
      setTarget(null)
      setAction(null)
    }
  }

  return (
    <List
      title={translate('admin.pages.posts')}
      canCreate={false}
      headerProps={{
        className: 'admin-list-header',
        subTitle: translate('admin.screens.moderation.browserSubtitle'),
      }}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        {error ? (
          <Alert type="error" showIcon message={error} closable onClose={() => setError(null)} />
        ) : null}

        <Table<PostRow>
          {...tableProps}
          rowKey="id"
          scroll={{ x: 960 }}
          sticky
          columns={[
            {
              title: translate('admin.columns.date'),
              dataIndex: 'created_at',
              render: (value: string) => dayjs(value).format('DD/MM/YYYY HH:mm'),
            },
            {
              title: translate('admin.columns.user'),
              dataIndex: ['profiles', 'full_name'],
              render: (value: string | undefined) => value ?? '—',
            },
            {
              title: translate('admin.columns.subject'),
              dataIndex: ['subjects', 'name_fr'],
              render: (value: string | undefined) => value ?? '—',
            },
            {
              title: translate('admin.screens.moderation.contentPreview'),
              dataIndex: 'body',
              ellipsis: true,
            },
            { title: translate('admin.screens.moderation.replyCount'), dataIndex: 'reply_count' },
            {
              title: translate('admin.columns.status'),
              dataIndex: 'is_hidden',
              render: (value: boolean) => (
                <Tag color={value ? 'warning' : 'success'}>
                  {translate(
                    value
                      ? 'admin.screens.moderation.contentHidden'
                      : 'admin.screens.moderation.contentVisible',
                  )}
                </Tag>
              ),
            },
            {
              title: translate('admin.columns.actions'),
              render: (_: unknown, row: PostRow) => (
                <CanAccess resource="posts" action="moderate">
                  <Space size="small">
                    {row.is_hidden ? (
                      <Button size="small" onClick={() => openAction(row, 'unhide')}>
                        {translate('admin.screens.moderation.unhide')}
                      </Button>
                    ) : (
                      <Button size="small" danger onClick={() => openAction(row, 'hide')}>
                        {translate('admin.screens.moderation.hide')}
                      </Button>
                    )}
                    <Button size="small" danger onClick={() => openAction(row, 'delete')}>
                      {translate('admin.screens.moderation.delete')}
                    </Button>
                  </Space>
                </CanAccess>
              ),
            },
          ]}
        />

        <Typography.Text type="secondary">
          {translate('admin.screens.moderation.queueFooter')}
        </Typography.Text>
      </Space>

      <ModerationActionDialog
        open={target !== null && action !== null}
        action={action ?? 'hide'}
        onConfirm={confirm}
        onCancel={() => {
          setTarget(null)
          setAction(null)
        }}
      />
    </List>
  )
}
